import clsx from 'clsx';
import { Button, ModalPopup, Stack, Text, Title } from 'components';
import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getClinicalEncountersPreview,
  getClinicalEncountersTypes,
  getClinicalEncountersStatuses
} from 'store/clinicalEncounters/clinicalEncountersActions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { axiosPrivate } from 'modules/networkTools/networkTools';
import dayjs from 'dayjs';

import style from './clinicalEncounters.module.scss';
import ClinicalEncounterForm from './clinicalEncounterForm';
import ClinicalEncountersFilters from './clinicalEncountersFilters';
import ClinicalEncountersTable from './clinicalEncountersTable';

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const downloadFile = async (clientId: string | null): Promise<any> => {
  const fileUploadUrl = `client/${clientId}/clinical-encounters/download`;
  const response = await axiosPrivate.get(fileUploadUrl);

  // convert to blob and set download link
  const file = new Blob([response.data], { type: 'text/csv' });
  const element = document.createElement('a');
  element.href = URL.createObjectURL(file);
  element.download = `Clinical Encounters_${dayjs().format('M-D-YY')}.csv`;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);

  return null;
};

const ClinicalEncountersModal: FC<Props> = ({ open, setOpen }) => {
  const { data } = useAppSelector((state) => state.clients.client);
  const { clientId } = useParams();
  const dispatch = useAppDispatch();
  const [showForm, setShowForm] = useState(false);
  const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const canEditClinicalEncounters = data?.clinicalEncountersEditingAllowed;

  useEffect(() => {
    if (clientId) {
      dispatch(getClinicalEncountersTypes({ clientId }));
      dispatch(getClinicalEncountersStatuses({ clientId }));
    }
  }, []);

  const handleModalClose = () => {
    if (clientId) dispatch(getClinicalEncountersPreview({ clientId }));
    setOpen(false);
  };

  return (
    <ModalPopup
      open={open}
      customClassInsideContainer={clsx(style.modalWrapper)}
      handleClose={() => {
        if (formDirty) {
          setUnsavedChangesModalOpen(true);
        } else {
          handleModalClose();
        }
      }}
    >
      <Stack width="100%" height="80vh">
        <Stack width="100%" flexDirection="row" justifyContent="space-between">
          <Stack
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="16px"
            mb="24px"
            mt="4px"
          >
            <Title text="Clinical Encounters" type="h3" />
            {!canEditClinicalEncounters && (
              <Text size="large" className={style.readOnly} weight="regular">
                Read-only
              </Text>
            )}
          </Stack>
          <Stack flexDirection="row" gap="12px" mr="48px">
            <Button
              type="button"
              theme="transparent"
              size="small"
              shape="circle"
              icon={<span className={clsx('material-icons')}>download</span>}
              handleOnClick={() => clientId && downloadFile(clientId)}
            />
            {canEditClinicalEncounters && (
              <Button
                size="small"
                shape="circle"
                handleOnClick={() => setShowForm(true)}
                disabled={showForm}
                icon={<span className="material-icons-outlined">add</span>}
                theme="primary"
                customStyleClass={style.iconButtonAdd}
              />
            )}
          </Stack>
        </Stack>
        <Stack width="100%" height="93%">
          {showForm && (
            <ClinicalEncounterForm
              handleCancel={() => {
                setShowForm(false);
                setFormDirty(false);
              }}
              setFormDirty={setFormDirty}
              unsavedChangesModalOpen={unsavedChangesModalOpen}
              setUnsavedChangesModalOpen={setUnsavedChangesModalOpen}
              handleModalClose={handleModalClose}
            />
          )}
          <ClinicalEncountersFilters />
          <ClinicalEncountersTable isDisabled={showForm} />
        </Stack>
      </Stack>
    </ModalPopup>
  );
};

export default ClinicalEncountersModal;
